import { PushNotificationChannel } from '../api/ChatbotAPI';
import type { APIMetainfo, PurchaseInfo } from '../api/ReplicantAPI';
import type { ABTestsDynamicConfig } from '../common/ReplicantABTests';
import type { ExchangeRates, PaymentSubscriptionStatus, UserMessageItem } from '../common/Types';
import type { EventHandlerMessage } from '../ReplicantEventHandlerMessages';
import { Message, Messages } from '../ReplicantMessages';
import type { SharedStates } from '../ReplicantSharedStates';
import { SocialGraphReceivedInteraction } from '../ReplicantSocialGraph';
import type SB from '../SchemaBuilder';
import type { ReplicationEntry } from '../server/ServerReplicant';
import { Diff, SnapshotUpdate } from '../server/StateRecovery';
import { TaskArgs, TaskType } from '../server/TaskRunner';
import type { VirtualStateDiff } from '../utils/VirtualState';
import type { WebPushSubscription } from '../utils/WebPush';
import type { AttributeMap } from './impl/DynamoDB/types';

export type ChatbotMetainfo = {
    /** Facebook application-scoped user ID aka ASID. */
    appScopedUserId?: string;

    /** ID of the platform user who was created using this web player's state. */
    convertedToUserId?: string;

    /** Instagram-scoped user ID. */
    instagramMessengerId?: string;

    subscribed?: boolean;
    messengerId?: string;
    hasUnsubscribed?: boolean;
    messagesSinceLastSession?: number;
    timeLastMessageSent?: number;
    timeLastSessionEnded?: number;
    timeFirstSubscribed?: number;

    /** The analytics payload of the last received platform chatbot message, stored for analytics purposes. */
    lastReceivedMessagePayload?: { [key: string]: unknown; timestamp: number };

    lastSessionId?: string;

    // Push notifications
    appleDeviceToken?: string;
    appleDeviceTokenUpdatedAt?: number;
    androidDeviceToken?: string;
    androidDeviceTokenUpdatedAt?: number;

    webPushSubscription?: WebPushSubscription;

    /** A record of when the most recent push notification was sent targeting this user, per channel. Written by the sender. */
    channelLastTargetedAt?: { [Channel in PushNotificationChannel]?: number };

    /** secret for native app bridge */
    nativeBridgeSecret?: string;

    /** for Zoom chatbot sending */
    email?: string;

    // For SMS notifications
    smsSubscribed?: boolean;

    /** Webhook-based purchase handlers store product IDs here for validation/consumption. */
    unconsumedProducts?: { [productId: string]: number }; // TODO: deprecate in favor of `purchaseHistory` once there are no more purchases which only exist in `unconsumedProducts`

    /** A log of IAP transactions added by webhook-based purchase flows. */
    purchaseHistory?: PurchaseInfo[];
};

export type MetainfoMVCC = {
    /** Used by the Facebook `onGameEnd` event handler, see https://playco.atlassian.net/browse/CORE-1073. */
    cancelledOnGameEndEventIds?: string[];
    scheduledActionNotifications?: { [notificationId: string]: ScheduledActionKey };
    systemScheduledActionNotifications?: { [notificationId: string]: ScheduledActionKey };
    paymentSubscription?: {
        lastUpdated: number;
        status: PaymentSubscriptionStatus;

        /** The 3rd party connected Stripe account used to create the subscription. `undefined` if created with the 1st party Playco Stripe account. */
        stripeAccountId?: string;
    };
};

export type Entry<TState, TSharedStates extends SharedStates = any> = {
    state: TState;

    /** Migration version number. */
    version: number;

    /** DB items schema version used to write the entry. */
    dbSchemaVersion?: number;

    /** Highest revision number among this entry's entry diff DB items. */
    rev: number;

    /** Main DB item revision number. */
    mainItemRev: number;

    metainfo: APIMetainfo; // API actions can modify this.
    metainfoMVCC: MetainfoMVCC; // Can be mutated from diferent places
    chatbotMetainfo?: ChatbotMetainfo; // Can be mutated from different places
    userSharedStates: UserSharedStateItems<TSharedStates>; // Mutated by API actions, stored in DB items separate from the main player entry

    /** Timestamp of user's latest login, or `undefined` if user has never logged in. */
    lastLoginAt?: number;

    /** Timestamp of the last message receive. */
    lastMessageReceivedAt?: number;

    /** Timestamp of latest db write. */
    lastUpdated: number;

    /** Last client-supplied timestamp. */
    lastInvokeTime?: number;
    createdAt: number;

    /** Timestamp of latest daily snapshot. */
    dailySnapshotAt?: number;
    /** Revision number of latest daily snapshot. */
    dailySnapshotRev?: number;
    /** Timestamp of latest weekly snapshot. */
    weeklySnapshotAt?: number;
    /** Revision number of latest weekly snapshot. */
    weeklySnapshotRev?: number;
    /** Timestamp of latest monthly snapshot. */
    monthlySnapshotAt?: number;
    /** Revision number of latest monthly snapshot. */
    monthlySnapshotRev?: number;
};

/**
 * The changes caused by applying a batch of actions to a user entry
 *
 * Written to DB on each replication request and applied back to user entry on DB read in order to minimize the number of full entry writes.
 */
export type EntryDiff = {
    dbSchemaVersion?: number;
    diff: VirtualStateDiff;
    lastInvokeTime?: number;
    lastUpdated: number;
    rev: number;

    dailySnapshotAt?: number;
    dailySnapshotRev?: number;
    weeklySnapshotAt?: number;
    weeklySnapshotRev?: number;
    monthlySnapshotAt?: number;
    monthlySnapshotRev?: number;
};

export type Changeset = {
    version: number;
    rev: number;
    date: Date;
    appVersion: string;
    replication: ChangesetReplication;
    messages: Omit<UserMessageItem, 'stateDiff'>[];
    diff?: Diff;
};
export type ChangesetResult = { value: any; async: boolean; failed?: boolean };
export type ChangesetReplication = { queue: ReplicationEntry[]; results: ChangesetResult[][] };

/** LINE Official Account chatbot broadcast scheduled through the admin API. */
export type LineChatbotBroadcast = {
    broadcastId: string;
    broadcastOpts: unknown;
    createdAt: number;
    error?: string;
    requestId?: string;
};

/** Single-use login token created with `api.loginLinks.createLoginToken`. */
export type LoginTokenItem = {
    userId: string;
    token: string;
    expiresAtMs: number;
};

export type ScheduledActionKey = { timeslotId: number; actionId: string };
export type ScheduledAction = {
    isSystemAction?: boolean;
    userId: string;
    notificationId: string;
    whenToExecute: number;
    actionName: string;
    actionArgs: unknown;
};

/** Either an ElasticSearch scroll ID or a DynamoDB key. */
export type LastEvaluatedKey = string | { id: { S: string } };

export type Tasks = { [id: string]: 'IN_PROGRESS' | 'COMPLETED' };

export type AdminTask<T extends TaskType = TaskType> = {
    id: string;
    task: T;
    args: TaskArgs<T>;
    ecsIds: string[];
    createdAt: number;
};

export type OTPVerificationItem = {
    /** Only defined when `intent: 'login'`. */
    clientAppName?: string;

    codeHash: string;
    consentText: string;
    id: string;
    intent: 'addReceiver' | 'login';
    receiver: string;
    type: 'sms';
};

export type SharedStateGlobalItem<TSharedStates extends SharedStates, TStateName extends keyof TSharedStates> = {
    createdAt: number;

    /** A map of recently reduced message IDs to timestamps, used to avoid reducing the same message twice. */
    recentMessageReductionLog?: { [messageId: string]: number };

    rev: number;
    state: SB.ExtractType<NonNullable<TSharedStates[TStateName]['schema']['global']>['schema']>;
    stateId: string;
    stateName: TStateName & string;
    updatedAt: number;
    version: number;
};

export type SharedStateMessageItem<TSharedStates extends SharedStates, TStateName extends keyof TSharedStates> = {
    args: TSharedStates[TStateName]['messages'] extends { [_: string]: { schema: SB.Schema<infer TArgs> } }
        ? TArgs
        : never;
    id: string;
    name: keyof TSharedStates[TStateName]['messages'] & string;
    sender: string;
    stateId: string;
    stateName: TStateName & string;
    timestamp: number;
};

export type SharedStateUserItem<TSharedStates extends SharedStates, TStateName extends keyof TSharedStates> = {
    stateId: string;
    stateName: TStateName & string;
    rev: number;
    state: SB.ExtractType<NonNullable<TSharedStates[TStateName]['schema']['user']>['schema']>;
    userId: string;
    version: number;
};

export type SharedStateResult<TSharedStates extends SharedStates, TStateName extends keyof TSharedStates> = {
    globalSharedState?: SharedStateGlobalItem<TSharedStates, TStateName>;
    sharedStateMessages: SharedStateMessageItem<TSharedStates, TStateName>[];
    userSharedStates: { [userId: string]: SharedStateUserItem<TSharedStates, TStateName> };
};

export type UserSharedStateItems<TSharedStates extends SharedStates> = {
    [K in keyof TSharedStates]: { [stateId: string]: SharedStateUserItem<TSharedStates, K> };
};

export type SocialGraphPlayerItem = {
    id: string;
    meta: { [key: string]: unknown };
    nonPlayerFriendIds: string[];
};
export type SocialGraphNonPlayerItem = {
    id: string;
    interactions: SocialGraphReceivedInteraction[];
    playerFriendIds: string[];
};

export type ABTestUserListConfig = {
    id: string;
    name: string;

    /** If specified, user list only applies to these specific tests. By default user list applies to all tests. */
    testIds?: string[];

    userIds: string[];
    bucketIds: string[];
};

export type ECPMItem = { timestamp: number; values: { [countryCode: string]: { [placementId: string]: number } } };
export type ExchangeRatesItem = { timestamp: number; rates: ExchangeRates };

export default interface DB<TState, TMessages extends Messages = any, TSharedStates extends SharedStates = any> {
    fetch: (
        id: string,
        opts?: {
            /**
             * Set to `true` to read user entry, metainfo and entry diff items consistently.
             * Messages are always read consistently regardless of the value.
             *
             * @default `false`
             */
            consistentRead?: boolean;

            /**
             * Skip any DB write operations like legacy item migrations.
             *
             * @default `false`
             */
            skipWrites?: boolean;
        },
    ) => Promise<{
        consumedCapacity: number;
        entry: Entry<TState> | null;
        entryDiffs: EntryDiff[];
        messages: UserMessageItem<TMessages>[];
    }>;

    write: (id: string, payload: Omit<Entry<TState>, 'mainItemRev'>, opts?: { noOverwrite: boolean }) => Promise<void>;
    writeToDBConditionally: (id: string, payload: Omit<Entry<TState>, 'mainItemRev'>, rev: number) => Promise<number>;

    /** Write a complete user entry item or an entry diff item, depending on the DB implementation. */
    writeEntryOrEntryDiff: (opts: {
        lastInvokeTime?: number;
        oldEntryDiffs: EntryDiff[];
        recoverySnapshotUpdate: SnapshotUpdate;
        snapshot: { diff: VirtualStateDiff; entry: Entry<TState> };
        userId: string;
    }) => Promise<{ itemByteSize: number }>;

    writeMessages: (userId: string, messages: Message<TMessages>[]) => Promise<void>;
    writeEventHandlerMessage: (userId: string, msg: EventHandlerMessage) => Promise<void>;
    deleteMessages: (userId: string, messages: { id: string }[]) => Promise<void>;
    fetchMessages: (userId: string, opts?: { consistentRead?: boolean }) => Promise<UserMessageItem<TMessages>[]>;

    // on changesets
    writeChangeset: (changeset: {
        id: string;
        entry: Omit<Entry<TState>, 'mainItemRev'>;
        date: Date;
        appVersion: string;
        replication: ChangesetReplication;
        messages: UserMessageItem[];
        diff: Diff;
        snapshotUpdate?: SnapshotUpdate;
    }) => Promise<void>;

    fetchChangeset: (id: string, rev: number, opts?: { consistentRead: boolean }) => Promise<Changeset | null>;

    /** @returns A list of changeset items between `fromRev` and `toRev` (both inclusive), sorted by ascending revision number. */
    fetchChangesetsBetween: (id: string, fromRev: number, toRev: number) => Promise<Changeset[]>;

    fetchChangesetsPage: (
        id: string,
        page?: number,
        num?: number,
    ) => Promise<{ items: Changeset[]; totalItemsCount: number }>;

    fetchChangesetsPageByDate: (
        id: string,
        date: string,
        num?: number,
    ) => Promise<{ items: Changeset[]; totalItemsCount: number; currentPage: number }>;

    /** Inactive user archival. */
    archive: {
        archiveUser: (
            userId: string,
            opts?: {
                /** Set to `true` to skip deleting the archived DB items. */
                skipDelete?: boolean;
            },
        ) => Promise<void>;
        restoreUserFromArchive: (userId: string) => Promise<AttributeMap[] | null>;
    };

    /** KV store exposed to players. */
    kvStore: {
        write: (key: string, value: string, now: number, opts?: { expiresInMs?: number }) => Promise<void>;
        fetch: (key: string, timestamp: number) => Promise<string | null>;
        delete: (key: string) => Promise<void>;
    };

    /** Single-use login token created with `api.loginLinks.createLoginToken`. */
    loginTokens: {
        writeLoginToken: (loginToken: LoginTokenItem) => Promise<void>;

        fetchLoginToken: (opts: { userId: string; token: string }) => Promise<LoginTokenItem | null>;

        deleteLoginToken: (opts: { userId: string; token: string }) => Promise<void>;
    };

    /** KV store for internal usage; only reads are exposed to players. */
    internalKvStore: {
        write: (key: string, value: string) => Promise<void>;
        writeBatch: (pairs: { [key: string]: string }) => Promise<void>;
        fetch: (key: string, opts?: { consistentRead?: boolean }) => Promise<string | null>;
        delete: (key: string) => Promise<void>;
    };

    lock: {
        /**
         * Try to write a lock with given unique identifier for given expiration duration (must be >5 seconds).
         *
         * @returns `true` if lock was acquired successfully and `false` is locking failed.
         */
        acquireTimestampLock: (id: string, now: number, expiresInMs: number) => Promise<boolean>;
    };

    metainfoMVCC: {
        addCancelledOnGameEndEventId(userId: string, eventId: string): Promise<MetainfoMVCC>;

        fetchMetainfoMVCC(userId: string, opts?: { consistentRead?: boolean }): Promise<MetainfoMVCC>;

        /** @param updates Notification IDs to set, or to delete if the value is `null`. */
        updateScheduledActionNotifications(
            userId: string,
            updates: {
                scheduledActionNotifications: { [notificationId: string]: ScheduledActionKey | null };
                systemScheduledActionNotifications: { [notificationId: string]: ScheduledActionKey | null };
            },
        ): Promise<void>;

        setPaymentSubscriptionStatus(
            userId: string,
            paymentSubscriptionStatus: { status: PaymentSubscriptionStatus; lastUpdated: number },
        ): Promise<MetainfoMVCC>;
        unsetPaymentSubscriptionStatus(userId: string): Promise<MetainfoMVCC>;

        getPaymentSubscriptionStatus(
            userId: string,
        ): Promise<{ status: PaymentSubscriptionStatus; lastUpdated: number } | undefined>;
    };

    // Chatbot metainfo
    chatbot: {
        fetchMetainfo: (id: string) => Promise<ChatbotMetainfo | null>;
        fetchMetainfos: (ids: string[]) => Promise<{ [id: string]: ChatbotMetainfo }>;

        setInstagramMessengerId: (opts: { userId: string; instagramMessengerId: string }) => Promise<ChatbotMetainfo>;

        setLastReceivedMessagePayload: (
            userId: string,
            lastReceivedMessagePayload: { [key: string]: unknown; timestamp: number },
        ) => Promise<ChatbotMetainfo>;

        // These modify the `chatbotMetainfo` subobject with optimistic locking and return the resulting object
        // in case it's required for immediate querying.
        setMessengerId: (id: string, msngrId: string) => Promise<ChatbotMetainfo>;
        markSubscribed: (id: string, subscribed: boolean, now: number) => Promise<ChatbotMetainfo>;
        markSubscribedAndResetMessagingSession: (
            id: string,
            subscribed: boolean,
            now: number,
        ) => Promise<ChatbotMetainfo>;
        markMessageSent: (id: string, now: number) => Promise<ChatbotMetainfo>;
        resetMessagingSession: (id: string, now: number) => Promise<ChatbotMetainfo>;
        setSessionId: (id: string, sessionId: string) => Promise<ChatbotMetainfo>;

        // Apple active push notification support.
        setAppleDeviceToken: (id: string, deviceToken: string | undefined, now: number) => Promise<ChatbotMetainfo>;
        setAndroidDeviceToken: (id: string, deviceToken: string | undefined, now: number) => Promise<ChatbotMetainfo>;

        updateTimeLastTargetedByChannelMessage: (
            id: string,
            channel: PushNotificationChannel,
            now: number,
        ) => Promise<ChatbotMetainfo>;

        // For the ios native app <-> browser iframe bridge.
        setNativeBridgeSecret: (id: string, bridgeSecret: string) => Promise<ChatbotMetainfo>;

        // For Zoom chatbot support
        setEmail: (id: string, email: string) => Promise<ChatbotMetainfo>;

        setWebPushSubscription: (
            id: string,
            pushSubscription: WebPushSubscription | undefined,
        ) => Promise<ChatbotMetainfo>;

        // SMS chatbots
        subscribeSms: (id: string) => Promise<ChatbotMetainfo>;
        unsubscribeSms: (id: string) => Promise<ChatbotMetainfo>;

        awardPurchase: (id: string, purchase: PurchaseInfo) => Promise<ChatbotMetainfo>;
        consumePurchase: (id: string, productId: string, now: number) => Promise<ChatbotMetainfo>;

        addToPurchaseHistory: (id: string, purchase: PurchaseInfo) => Promise<ChatbotMetainfo>;

        set: (id: string, callback: (oldChatbotMetainfo: ChatbotMetainfo) => void) => Promise<ChatbotMetainfo>;
    };

    entryDiff: {
        /** @throws If entry diff with the same revision already exists. */
        write: (userId: string, entryDiff: EntryDiff) => Promise<{ itemByteSize: number }>;

        deleteBatch: (userId: string, revs: number[]) => Promise<void>;
    };

    scheduledActions: {
        writeBatch: (scheduledActions: { key: ScheduledActionKey; action: ScheduledAction }[]) => Promise<void>;
        fetch: (key: ScheduledActionKey) => Promise<ScheduledAction | null>;
        delete: (key: ScheduledActionKey) => Promise<void>;
        deleteBatch: (keys: ScheduledActionKey[]) => Promise<void>;
        fetchAllForTimeslot: (
            timeslotId: number,
            limitPerShard: number,
            shards: number[],
            continuationTokens?: { [timeslotId: string]: AttributeMap },
        ) => Promise<{
            actions: { [actionId: string]: ScheduledAction };
            continuationTokens?: { [timeslotId: string]: AttributeMap };
        }>;
    };

    sharedStates: {
        counters: {
            fetchCounters: (opts: {
                stateId: string;
                stateName: string;
            }) => Promise<{ [counterName: string]: { value: number; updatedAt: number } | undefined }>;

            increaseCounters: (opts: {
                stateId: string;
                stateName: string;
                counters: { [counterName: string]: number };
                now: number;
            }) => Promise<void>;

            setCounter: (opts: {
                stateId: string;
                stateName: string;
                counterName: string;
                value: number;
                now: number;
            }) => Promise<void>;
        };

        /** @returns A single complete shared state including global state and all constituent user states. */
        fetchSharedState: <TStateName extends keyof TSharedStates>(opts: {
            stateId: string;
            stateName: string;
            consistentRead?: boolean;
        }) => Promise<{ sharedState: SharedStateResult<TSharedStates, TStateName> | null; consumedCapacity: number }>;

        fetchGlobalSharedState: <TStateName extends keyof TSharedStates>(opts: {
            stateId: string;
            stateName: string;
            consistentRead?: boolean;
        }) => Promise<SharedStateGlobalItem<TSharedStates, TStateName> | null>;

        /** @returns All user shared states owned by `userId`. */
        fetchUserStates: (userId: string) => Promise<UserSharedStateItems<TSharedStates>>;

        migrateLegacyUserSharedStates: (legacyItems: SharedStateUserItem<any, any>[]) => Promise<void>;

        writeGlobalSharedState: <TStateName extends keyof TSharedStates>(
            payload: SharedStateGlobalItem<TSharedStates, TStateName>,
        ) => Promise<void>;

        writeSharedStateMessages: <TStateName extends keyof TSharedStates>(
            messages: SharedStateMessageItem<TSharedStates, TStateName>[],
        ) => Promise<void>;

        writeUserSharedState: <TStateName extends keyof TSharedStates>(
            payload: SharedStateUserItem<TSharedStates, TStateName>,
            opts?: {
                /** @default false */
                useLegacyKey?: boolean;
            },
        ) => Promise<void>;

        deleteSharedStateMessages: (
            messages: { messageId: string; stateId: string; stateName: string }[],
        ) => Promise<void>;

        deleteUserSharedState: (opts: {
            userId: string;
            stateId: string;
            stateName: string;
            useLegacyKey?: boolean;
        }) => Promise<void>;
    };

    /** State recovery snapshots. */
    snapshots: {
        fetchSnapshot: (id: string, rev: number) => Promise<Entry<{ [key: string]: unknown }> | null>;

        /** @returns State recovery snapshot items sorted by ascending revision number. */
        fetchSnapshots: (id: string) => Promise<(Entry<{ [key: string]: unknown }> & { expirationDate?: number })[]>;
    };

    socialGraph: {
        getPlayer: (playerId: string) => Promise<SocialGraphPlayerItem | null>;
        getPlayers: (playerIds: string[]) => Promise<{ [playerId: string]: SocialGraphPlayerItem | undefined }>;
        getNonPlayers: (nonPlayerIds: string[]) => Promise<SocialGraphNonPlayerItem[]>;

        createOrUpdatePlayer: (player: SocialGraphPlayerItem) => Promise<void>;
        createInteractions: (receiverId: string, interactions: SocialGraphReceivedInteraction[]) => Promise<void>;

        /** Add `playerFriendId` to `nonPlayerId`'s set of player friends. */
        addPlayerFriend: (nonPlayerId: string, playerFriendId: string) => Promise<void>;

        /** Remove `playerFriendId` from `nonPlayerId`'s set of player friends. */
        removePlayerFriend: (nonPlayerId: string, playerFriendId: string) => Promise<void>;
    };

    // Task queue deduplication: used to enforce exactly-once processing
    tasks: {
        completeTask: (id: string, now: number) => Promise<void>;

        fetchTasks: (ids: string[], now: number, expiresInMs: number) => Promise<Tasks>;

        /**
         * Try to write a lock with given unique identifier for given expiration duration.
         *
         * @returns Lock status: if true, lock is in place and task processing can take place.
         * Otherwise task with `id` is already locked and should not be processed.
         */
        tryWriteLock: (id: string, now: number, expiresInMs: number) => Promise<boolean>;
    };

    /** Related to OTP login: link user ID to phone number. */
    accountLink: {
        getPhoneNumber: (userId: string) => Promise<string | null>;
        getUserId: (opts: { clientAppName?: string; phoneNumber: string }) => Promise<string | null>;
        linkUserId: (opts: {
            clientAppName?: string;
            consentText: string;
            now: number;
            phoneNumber: string;
            userId: string;
        }) => Promise<void>;
        setNotificationConsentText: (opts: { userId: string; notificationConsentText: string }) => Promise<void>;
    };

    adminTasks: {
        listTasks: () => Promise<AdminTask[]>;
        getTask: (id: string) => Promise<AdminTask | null>;
        createTask: <T extends TaskType>(opts: {
            id: string;
            ecsIds: string[];
            args: TaskArgs<T>;
            task: T;
            now: number;
        }) => Promise<AdminTask>;

        listWorkerProgresses: (opts: {
            taskId: string;
        }) => Promise<{ itemsProcessed: number; lastEvaluatedKey?: LastEvaluatedKey }[]>;
        getWorkerProgress: (opts: {
            taskId: string;
            workerId: number;
        }) => Promise<{ itemsProcessed: number; lastEvaluatedKey?: LastEvaluatedKey } | null>;
        setWorkerProgress: (opts: {
            taskId: string;
            workerId: number;
            itemsProcessed: number;
            lastEvaluatedKey?: LastEvaluatedKey;
        }) => Promise<void>;
    };

    abTests: {
        userLists: {
            get: () => Promise<ABTestUserListConfig[]>;
            set: (payload: ABTestUserListConfig[]) => Promise<void>;
        };

        get: () => Promise<ABTestsDynamicConfig>;
        set: (config: ABTestsDynamicConfig) => Promise<void>;
    };

    ecpm: {
        set: (item: ECPMItem) => Promise<void>;
        get: () => Promise<ECPMItem | null>;
    };

    exchangeRates: {
        get: () => Promise<ExchangeRatesItem>;
        set: (exchangeRates: ExchangeRatesItem) => Promise<void>;
        setTimestamp: (timestamp: number) => Promise<void>;
    };

    /** LINE Official Account chatbot broadcasts scheduled through the admin API. */
    lineChatbotBroadcasts: {
        addBroadcast: (broadcast: LineChatbotBroadcast) => Promise<void>;

        /** @returns Broadcasts sent within the past 7 days in descending order of creation. */
        getBroadcasts: () => Promise<LineChatbotBroadcast[]>;
    };

    otp: {
        getVerification: (
            verification: Pick<OTPVerificationItem, 'id' | 'intent'>,
            now: number,
        ) => Promise<OTPVerificationItem | null>;
        createVerification: (verification: OTPVerificationItem, now: number) => Promise<void>;
    };

    // Danger zone -- nuke db and user. Used only in dev, should always be disabled in production.
    delete: (id: string) => Promise<void>;
    nukeDB: () => Promise<void>;
}

export class ConditionalWriteError extends Error {
    readonly code = 'ConditionalCheckFailedException';

    constructor(readonly newRevision: number) {
        super(
            'Cannot write DB item: revision number mismatch. This revision may have already been written due to a race condition.',
        );
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export function hasMigratedLegacyUserSharedStateItems(entry: { dbSchemaVersion?: number }): boolean {
    return entry.dbSchemaVersion !== undefined && entry.dbSchemaVersion >= 1;
}
